import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check_white.svg';
import { getDomainConfig } from '../../lib/domainConfig';
import * as types from '../../types';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import styles from './HardwareFirstTrialModal.module.scss';

/** the hardware-first 30-day terms: what the tracker, the first year and the renewal cost */
export interface HardwareFirstTrialTerms {
  listPrice: string;
  firstYear: string;
  renewal: string;
  shippingPrice: string | null;
}

interface HardwareFirstTrialModalProps {
  trigger: JSX.Element;
  productCategory: types.ProductCategory;
  terms: HardwareFirstTrialTerms;
}

const CHECK_KEYS = [
  'hardware_first_trial_modal_check_return',
  'hardware_first_trial_modal_check_tracking',
  'hardware_first_trial_modal_check_warranty',
];

/** The hardware-first trial's terms as a sheet on mobile and a centred card on desktop. */
export default function HardwareFirstTrialModal({ trigger, productCategory, terms }: HardwareFirstTrialModalProps) {
  const { t } = useTranslation();
  const { baseDomain } = getDomainConfig();
  const isMini = productCategory === types.ProductCategory.MINI;

  return (
    <SimpleDismissableModal trigger={trigger} containerClassName={styles.modal} closeIconClassName={styles.closeIcon}>
      {(close) => (
        <div className={styles.container}>
          <span aria-hidden="true" className={styles.handle} />
          <h2 className={styles.title}>{t('hardware_first_trial_modal_title')}</h2>
          <p className={styles.subtitle}>
            <Trans
              components={{ bold: <strong /> }}
              i18nKey={isMini ? 'hardware_first_trial_modal_subtitle_mini' : 'hardware_first_trial_modal_subtitle_s3'}
            />
          </p>
          <ul className={styles.checks}>
            {CHECK_KEYS.map((key) => (
              <li className={styles.check} key={key}>
                <span className={styles.checkIcon}>
                  <CheckIcon />
                </span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
          <dl className={styles.panel}>
            {terms.shippingPrice && (
              <div className={styles.panelRow}>
                <dt className={styles.label}>{t('hardware_first_trial_modal_today')}</dt>
                <dd className={styles.value}>
                  {t('hardware_first_trial_modal_today_value', { shippingPrice: terms.shippingPrice })}
                </dd>
              </div>
            )}
            <div className={styles.panelRow}>
              <dt className={styles.label}>{t('hardware_first_trial_modal_keep')}</dt>
              <dd className={styles.value}>
                {t('hardware_first_trial_modal_keep_value', { listPrice: terms.listPrice, firstYear: terms.firstYear })}
              </dd>
            </div>
            <div className={styles.panelRow}>
              <dt className={styles.label}>{t('hardware_first_trial_modal_return')}</dt>
              <dd className={styles.value}>{t('hardware_first_trial_modal_return_value')}</dd>
            </div>
          </dl>
          <p className={styles.fine}>{t('hardware_first_trial_modal_fine_print', { renewal: terms.renewal })}</p>
          <p className={styles.fine}>
            <Trans
              components={{
                tos: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a href={`https://${baseDomain}/tos`} rel="noopener noreferrer" target="_blank" />
                ),
              }}
              i18nKey="hardware_first_trial_modal_tos"
            />
          </p>
          <button className={styles.cta} onClick={close} type="button">
            {t('hardware_first_trial_modal_got_it')}
          </button>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
